/**
 * 1OS Date Policy — shared date utilities for the frontend.
 *
 * Invariants (never violate):
 *   1. Frontend handles display + selection in LOCAL timezone.
 *   2. Backend interprets dates in OUTLET timezone.
 *   3. Never use UTC for business-day logic.
 *   4. APIs accept YYYY-MM-DD strings (date-only), never datetimes.
 *
 * Why this file exists
 * --------------------
 * `Date.prototype.toISOString()` always emits the UTC calendar date. For
 * users east of UTC (IST = UTC+5:30) a local-midnight Date like
 * `new Date(2026, 4, 1)` shifts to "2026-04-30T18:30:00.000Z" — slicing
 * the first 10 chars silently returns the WRONG day. This has caused
 * two founder-visible incidents already:
 *   • "This Month" preset starting on the last day of the prior month.
 *   • Custom date picker refusing to land on the first day of any month.
 *
 * Rule: always use `fmtLocalISO(date)` for any UI-selected or
 * preset-derived date that gets sent to the backend.
 *
 * An ESLint `no-restricted-syntax` rule in `.eslintrc` blocks direct
 * `.toISOString()` usage in `src/` so this never regresses.
 */

/**
 * Format a Date as `YYYY-MM-DD` using its LOCAL year/month/day.
 * Never round-trips through UTC — zero timezone risk.
 *
 * @param {Date} date
 * @returns {string}
 */
export function fmtLocalISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Today in local TZ as `YYYY-MM-DD`. */
export function todayISO() {
  return fmtLocalISO(new Date());
}

/**
 * `n` days ago in local TZ as `YYYY-MM-DD`.
 * Positive `n` → past; negative → future.
 */
export function isoMinusDays(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return fmtLocalISO(d);
}
